import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Button, Skeleton, Divider, Alert } from '@mui/material';
import { useAuthStore } from '../store/authStore';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
// Удаляем импорт MainLayout, так как он уже применяется через маршрутизацию
import { formatDate } from '../utils/dateUtils';
import { QRCodeSVG } from 'qrcode.react';
import { CardGiftcard } from '@mui/icons-material';

const CouponDetailPage = () => {
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: userInfo } = useAuthStore();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!userInfo) {
      navigate('/login');
      return;
    }

    const fetchCouponDetails = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get('/api/coupons/my-coupons', {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        const foundCoupon = data.find(c => c.id === id);
        if (foundCoupon) {
          setCoupon(foundCoupon);
          setError(null);
        } else {
          setError('Купон не найден');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Не удалось загрузить информацию о купоне');
        console.error('Error fetching coupon details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCouponDetails();
  }, [userInfo, navigate, id]);

  const handleBack = () => {
    navigate('/my-coupons');
  };

  return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        <Button 
          variant="outlined" 
          onClick={handleBack} 
          sx={{ mb: 3 }}
        >
          Назад к списку купонов
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Paper sx={{ p: 4 }}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={30} />
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <Skeleton variant="rectangular" width={250} height={250} />
            </Box>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </Paper>
        ) : coupon ? (
          <Paper 
            sx={{ 
              p: 4, 
              position: 'relative',
              ...(coupon.isUsed && {
                opacity: 0.8,
                backgroundColor: '#f5f5f5',
              })
            }}
          >
            {coupon.isUsed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-30deg)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 0, 0, 0.7)',
                  color: 'white',
                  padding: '10px 30px',
                  borderRadius: '5px',
                  fontWeight: 'bold',
                  fontSize: '2rem',
                  textTransform: 'uppercase',
                }}
              >
                Использован
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              {!coupon.isUsed && (
                <CardGiftcard color="secondary" sx={{ mr: 2, fontSize: 40 }} />
              )}
              <Typography variant="h4" component="h1">
                {coupon.coupon.name}
              </Typography>
            </Box>
            
            <Typography variant="h5" color="primary" gutterBottom align="center">
              {coupon.coupon.value} ₽
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, position: 'relative' }}>
              <QRCodeSVG 
                value={coupon.qrCode} 
                size={250} 
                level="H" 
                includeMargin={true}
              />
              {!coupon.isUsed && (
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: -15, 
                  right: 'calc(50% - 140px)', 
                  bgcolor: 'secondary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                    },
                    '50%': {
                      transform: 'scale(1.1)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                    },
                  },
                }}>
                  <CardGiftcard sx={{ fontSize: 32 }} />
                </Box>
              )}
            </Box>
            
            <Typography variant="body1" paragraph align="center">
              {coupon.coupon.description || 'Нет описания'}
            </Typography>
            
            <Box sx={{ mt: 4 }}>
              <Alert severity="info">
                Предъявите этот QR-код администратору при посещении объекта для получения скидки. 
                Купон можно использовать только один раз при одном посещении.
              </Alert>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              {coupon.isUsed && (
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid #ffcccc', 
                  borderRadius: 1, 
                  bgcolor: '#fff8f8',
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle1" color="error" fontWeight="bold">
                    Купон использован
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Дата использования: {formatDate(coupon.usedAt)}
                  </Typography>
                </Box>
              )}
              {coupon.coupon.expiresAt && (
                <Typography variant="body2" color="error" align="center">
                  Действителен до: {formatDate(coupon.coupon.expiresAt)}
                </Typography>
              )}
            </Box>
          </Paper>
        ) : (
          <Alert severity="warning">Купон не найден</Alert>
        )}
      </Container>
  );
};

export default CouponDetailPage;
